// DevHub UI kit — filter bar.
//
// The search input + optional ODX dropdowns + clear button that the docs and
// catalog grids each re-invent (mirrors `devhub-docs-filter-bar`). Dropdowns use
// the shared OdxSelect (a real ODX popover, not the native <select>).
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { OdxSelect, OdxSelectOption } from '@internal/plugin-isdp-common/ui';
import { kitMessages } from '../../i18n/kit';
import { noBrowserAutofill } from '../noBrowserAutofill';
import './FilterBar.css';

export interface FilterBarSelect {
  value: string;
  onChange: (value: string) => void;
  options: Array<OdxSelectOption | string>;
  ariaLabel: string;
  placeholder?: string;
}

export interface FilterBarProps {
  search: string;
  onSearch: (value: string) => void;
  /** Optional dropdown filters rendered after the search input. */
  selects?: FilterBarSelect[];
  /** When provided, renders a "Clear" button that calls this. */
  onClear?: () => void;
  /** Override the search input placeholder. */
  searchPlaceholder?: string;
}

export const FilterBar = ({
  search,
  onSearch,
  selects,
  onClear,
  searchPlaceholder,
}: FilterBarProps) => {
  const { t } = useTranslationRef(kitMessages);
  return (
    <div className="devhub-filter-bar">
      <div className="devhub-filter-bar__search">
        <odx-icon name="core::search" size="sm" />
        <input
          className="devhub-filter-bar__input"
          type="text"
          placeholder={searchPlaceholder ?? t('filter.searchPlaceholder')}
          value={search}
          onChange={e => onSearch(e.target.value)}
          {...noBrowserAutofill}
        />
      </div>
      {selects?.map(sel => (
        <OdxSelect
          key={sel.ariaLabel}
          className="devhub-filter-bar__select"
          value={sel.value}
          onChange={sel.onChange}
          options={sel.options}
          ariaLabel={sel.ariaLabel}
          placeholder={sel.placeholder}
        />
      ))}
      {onClear && (
        <odx-button
          type="button"
          variant="ghost"
          size="sm"
          className="devhub-filter-bar__clear"
          onClick={onClear}
        >
          {t('filter.clear')}
        </odx-button>
      )}
    </div>
  );
};
